import { createShapeId } from 'tldraw';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import { flushBoardDocumentOps } from './persistBoardDocumentOps';
import {
  buildConversationPinPayload,
  buildExchangePinPayload,
  buildLatestExchangePinPayload,
  extractPinQuestionTitle,
} from './conversationPinPayload';
import {
  SUGGEST_PIN_WIDTH,
  SUGGEST_PIN_MIN_HEIGHT,
  SUGGEST_PIN_GRID_STRIDE,
} from '../constants/suggestPinLayout';

export const CONVERSATION_PIN_TYPE = 'conversation-pin';

export {
  buildConversationPinPayload,
  buildExchangePinPayload,
  buildLatestExchangePinPayload,
  extractPinQuestionTitle,
};

function getNextConversationPinPosition(editor) {
  const viewport = editor.getViewportPageBounds?.();
  const existing = editor
    .getCurrentPageShapes()
    .filter(shape => shape.type === CONVERSATION_PIN_TYPE);
  const index = existing.length;
  const baseX = (viewport?.x || 0) + Math.max(40, (viewport?.w || 900) * 0.55);
  const baseY = (viewport?.y || 0) + 80;

  return {
    x: baseX + (index % 2) * SUGGEST_PIN_GRID_STRIDE,
    y: baseY + Math.floor(index / 2) * SUGGEST_PIN_GRID_STRIDE,
  };
}

function conversationPinShapeId({ conversationId, pinKind, messageAt }) {
  const safeConversation = String(conversationId).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 36);
  const safeMessage = String(messageAt || 'all').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 36);
  return createShapeId(`conversation-pin-${pinKind}-${safeConversation}-${safeMessage}`);
}

export function createConversationPinShape(editor, payload, { saveSnapshot } = {}) {
  if (!editor || !payload?.conversationId) return null;

  const shapeId = conversationPinShapeId(payload);
  const existing = editor.getShape(shapeId);
  const position = getNextConversationPinPosition(editor);
  const before = extractBoardDocumentSnapshot(editor);

  editor.run(() => {
    editor.markHistoryStoppingPoint('before-conversation-pin');

    if (existing) {
      editor.updateShape({
        id: shapeId,
        type: CONVERSATION_PIN_TYPE,
        props: {
          title: payload.title || existing.props.title,
          snippet: payload.snippet || existing.props.snippet,
          imageUrl: payload.imageUrl || existing.props.imageUrl || '',
          sourceUrl: payload.sourceUrl || existing.props.sourceUrl || '',
          conversationId: payload.conversationId,
          pinKind: payload.pinKind || 'conversation',
          messageAt: payload.messageAt || '',
          w: SUGGEST_PIN_WIDTH,
        },
      });
      editor.setSelectedShapes([shapeId]);
      editor.zoomToSelection({ animation: { duration: 250 } });
    } else {
      editor.createShape({
        id: shapeId,
        type: CONVERSATION_PIN_TYPE,
        x: position.x,
        y: position.y,
        props: {
          w: SUGGEST_PIN_WIDTH,
          h: SUGGEST_PIN_MIN_HEIGHT,
          title: payload.title || 'Board AI chat',
          snippet: payload.snippet || '',
          imageUrl: payload.imageUrl || '',
          sourceUrl: payload.sourceUrl || '',
          conversationId: payload.conversationId,
          pinKind: payload.pinKind || 'conversation',
          messageAt: payload.messageAt || '',
        },
      });
      editor.setSelectedShapes([shapeId]);
      editor.zoomToSelection({ animation: { duration: 250 } });
    }
  });

  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  return shapeId;
}
